import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { PageHeader, Loading } from "../components/Layout";
import { api, errMsg } from "../lib/api";
import { addressText, CLINIC_TYPE } from "./Clinics";
import { PetPromo } from "./VetCuraPet";
import { ClinicLogo } from "../components/ClinicLogo";

export const CITIES = [["koebenhavn", "København"], ["aarhus", "Aarhus"], ["odense", "Odense"], ["aalborg", "Aalborg"], ["esbjerg", "Esbjerg"], ["roskilde", "Roskilde"], ["randers", "Randers"], ["kolding", "Kolding"], ["vejle", "Vejle"], ["slagelse", "Slagelse"]];

export const CitiesIndex = () => (
  <div data-testid="cities-page">
    <PageHeader eyebrow="Dyrlæger efter by" title="Find dyrlæge i din by">Se dyreklinikker og dyrehospitaler i de største danske byer – med adresse, hjemmeside og verificerede priser, hvor vi har dem.</PageHeader>
    <ul className="container-vp py-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {CITIES.map(([s, n]) => <li key={s}><Link to={`/dyrlaege/${s}`} className="card-vp p-4 block font-semibold text-[#153C3A] hover:shadow-md" data-testid={`city-link-${s}`}>Dyrlæge i {n}</Link></li>)}
    </ul>
  </div>
);

export default function CityPage() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setD(null);
    api.get(`/seo/city/${slug}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [slug]);
  if (error) return <div className="container-vp py-16 text-[#52706D]" data-testid="city-error">{error}</div>;
  if (!d) return <Loading />;
  return (
    <div data-testid="city-page">
      <PageHeader eyebrow={`Dyrlæge ${d.name}`} title={`Find dyrlæger og dyreklinikker i ${d.name}`}>
        VetPris har {d.clinics.length} dyreklinikker i og omkring {d.name}, og {d.price_clinics} af dem har verificerede priser.
      </PageHeader>
      <div className="container-vp py-10 grid lg:grid-cols-[1fr_300px] gap-8">
        <ul className="grid gap-3 md:grid-cols-2 content-start" data-testid="city-clinics">
          {d.clinics.length === 0 && <li className="text-sm text-[#52706D]" data-testid="city-empty">Vi har endnu ingen klinikker i {d.name} i oversigten. Se <Link to="/dyreklinikker" className="text-[#087F72] hover:underline">alle dyreklinikker</Link> eller en af de andre byer.</li>}
          {d.clinics.map((c) => (
            <li key={c.id}><Link to={`/dyreklinikker/${c.slug}`} className="card-vp p-4 block hover:shadow-md" data-testid={`city-clinic-${c.slug}`}>
              <div className="flex items-center gap-3"><ClinicLogo url={c.logo_url} name={c.name} size="sm" testid={`city-logo-${c.slug}`} /><p className="font-heading font-bold text-[#153C3A]">{c.name}</p></div>
              <p className="text-xs text-[#52706D]">{CLINIC_TYPE[c.clinic_type] || "Dyreklinik"}{c.distance_km != null ? ` · ${c.distance_km.toLocaleString("da-DK")} km fra centrum` : ""}</p>
              {c.locations.map((l) => <p key={l.id} className="text-sm text-[#52706D] mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" />{addressText(l)}</p>)}
              <p className="text-xs font-semibold text-[#087F72] mt-2">{c.price_count ? `${c.price_count} verificerede priser` : "Ingen verificerede priser endnu"}</p>
            </Link></li>))}
        </ul>
        <aside className="space-y-6 text-sm">
          <div className="card-vp p-4"><h2 className="font-bold text-[#153C3A]">Sammenlign priser i {d.name}</h2>
            <div className="flex gap-2 mt-2"><Link className="btn-primary btn-sm" to={`/sammenlign?species=dog&location=${encodeURIComponent(d.name)}`}>Hund</Link><Link className="btn-outline btn-sm" to={`/sammenlign?species=cat&location=${encodeURIComponent(d.name)}`}>Kat</Link></div></div>
          {d.treatments.length > 0 && <div><h2 className="font-bold text-[#153C3A]">Behandlinger med priser i området</h2>
            <ul className="mt-2 space-y-1" data-testid="city-treatments">{d.treatments.map((t) => <li key={t.slug}><Link to={`/behandlinger/${t.slug}`} className="text-[#087F72] hover:underline">{t.name}</Link> <span className="text-xs text-[#52706D]">({t.count})</span></li>)}</ul></div>}
          <Link to={`/akut-dyrlaege/${d.slug}`} className="card-vp p-4 block font-semibold text-[#B42318] hover:shadow-md" data-testid="city-akut-link">Akut dyrlæge i {d.name} – døgnåbne steder og vagttelefoner</Link>
          <div><h2 className="font-bold text-[#153C3A]">Andre byer</h2>
            <ul className="mt-2 grid grid-cols-2 gap-1">{d.others.map((o) => <li key={o.slug}><Link to={`/dyrlaege/${o.slug}`} className="text-[#087F72] hover:underline">{o.name}</Link></li>)}</ul></div>
        </aside>
      </div>
      <div className="container-vp pb-12"><PetPromo testid="city-vetcurapet-promo" /></div>
    </div>
  );
}
